import React from 'react'
import { Tooltip } from '@mui/material'
import { TooltipProps } from '@mui/material/Tooltip/Tooltip'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'

import { ClassesType } from '../types'

const useStyles = makeStyles(
  theme => ({
    popper: {},
    tooltip: {
      minWidth: 45,
      marginLeft: theme.spacing(-1.5),
      marginBottom: 14,
      marginTop: 0,
      backgroundColor: theme.colors.badgeColor,
      boxShadow: '0 2px 5px 0 rgba(0,0,0,0.3)',
      border: `2px solid ${theme.colors.title}`,
      borderRadius: 4,
      color: theme.colors.secondaryText,
      fontWeight: 400,
      position: 'relative',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      '&::before, &::after': {
        top: '100%',
        left: 0,
        border: 'solid transparent',
        content: '" "',
        height: 0,
        width: 0,
        position: 'absolute',
      },
      '&::before': {
        borderTopColor: theme.colors.title,
        borderWidth: 13,
        marginLeft: 9,
      },
      '&::after': {
        borderTopColor: theme.colors.badgeColor,
        borderWidth: 10,
        marginLeft: 12,
      },
    },
    tooltipBottom: {
      marginBottom: 0,
      marginTop: 14,
      '&::before, &::after': {
        top: 0,
      },
      '&::before': {
        transform: 'rotate(180deg)',
        marginTop: -26,
      },
      '&::after': {
        transform: 'rotate(180deg)',
        marginTop: -20,
      },
    },
    tooltipRight: {
      marginRight: theme.spacing(-1.5),
      '&::before, &::after': {
        right: 0,
        left: 'unset',
      },
      '&::before': {
        marginRight: 9,
      },
      '&::after': {
        marginRight: 12,
      },
    },
    tooltipCenter: {
      '&::before': {
        right: 0,
        margin: 'auto',
      },
      '&::after': {
        right: 0,
        margin: 'auto',
      },
    },
  }),
  { name: 'PuiBaseTooltip' },
)

export interface PuiBaseTooltipProps extends Omit<TooltipProps, 'title'> {
  readonly classes?: ClassesType<typeof useStyles>
  readonly tooltipPlacement?: TooltipProps['placement']
  readonly tooltipText: React.ReactNode
}

const PuiBaseTooltip = ({
  classes: classesProp,
  children,
  tooltipPlacement = 'top-start',
  tooltipText,
  ...rest
}: PuiBaseTooltipProps) => {
  const classes = useStyles({ classes: classesProp })

  return (
    <Tooltip
      classes={{
        popper: classes.popper,
        tooltip: classNames(classes.tooltip, {
          [classes.tooltipRight]: tooltipPlacement === 'top-end',
          [classes.tooltipCenter]: tooltipPlacement === 'top',
        }),
        tooltipPlacementBottom: classes.tooltipBottom,
      }}
      placement={tooltipPlacement}
      title={tooltipText}
      {...rest}
    >
      {children}
    </Tooltip>
  )
}

export default PuiBaseTooltip
